import React, { useEffect, useRef, useState } from 'react'

import {
  LoadingOutlined,
  PlusCircleFilled,
} from '@ant-design/icons'

import { TrashCanIcon } from '../../../icons/TrashCan'
import { EditIcon } from '../../../icons/Edit'
import { AddCameraIcon } from '../../../icons/AddCamera'
import { AddFolderIcon } from '../../../icons/AddFolder'
import { MoreIcon } from '../../../icons/More'
import { Spin } from 'antd'

import {
  GroupNodeHeader,
  GroupNodeHeaderLeft,
  GroupNodeWrapper,
  GroupNodeHeaderAction,
  GroupNodeHeaderIcon,
  GroupNodeHeaderName,
  NodeActionItem,
  NodeActionItems,
  NodeActionPopover,
  DeviceNodeAction,
} from '../Composable'
import { useAddSubgroup, useUpdateDevice } from '../../../hooks/socket/cameras'

import './styles.scss'

export const defaultAllowActions = {
  pin: true,
  remove: true,
  addSubgroup: true,
  addCamera: true,
  editName: true,
}

export const If = ({
  condition,
  children,
}) => {
  if (condition) {
    return children
  }

  return null
}


export const GroupNodeMore = ({ actions = defaultAllowActions, onAction }) => {
  const [open, setOpen] = useState(false)

  const isEmpty = !(
    actions.addCamera ||
    actions.addSubgroup ||
    actions.editName ||
    actions.remove
  )

  const hide = () => {
    setOpen(false)
  }

  const emitAction = (action) => {
    hide()
    onAction(action)
  }

  if (isEmpty) {
    return null
  }

  const content = () => {
    return (
      <NodeActionItems>
        <If condition={actions.addSubgroup}>
          <NodeActionItem
            icon={<AddFolderIcon />}
            onClick={() => emitAction('addSubgroup')}
            label="Add group"
          />
        </If>
        <If condition={actions.editName}>
          <NodeActionItem
            icon={<EditIcon />}
            onClick={() => emitAction('editName')}
            label="Edit"
          />
        </If>
        <If condition={actions.addCamera}>
          <NodeActionItem
            icon={<AddCameraIcon />}
            onClick={() => emitAction('addCamera')}
            label="Add camera"
          />
        </If>
        <If condition={actions.remove}>
          <NodeActionItem
            danger
            icon={<TrashCanIcon />}
            onClick={() => emitAction('remove')}
            label="Remove group"
          />
        </If>
      </NodeActionItems>
    )
  }

  return (
    <NodeActionPopover
      trigger="click"
      visible={open}
      onVisibleChange={setOpen}
      content={content()}
      placement="rightTop"
    >
      <GroupNodeHeaderAction className={open ? 'active' : ''}>
        <MoreIcon />
      </GroupNodeHeaderAction>
    </NodeActionPopover>
  )
}

const defaultDeviceActions = {
  remove: true,
  edit: true,
}

const defaultDeviceLabels = {
  remove: 'Remove',
  edit: 'Edit',
}

export const DeviceNodeMore = ({ actions = defaultDeviceActions, onAction, labels = defaultDeviceLabels }) => {
  const [open, setOpen] = useState(false)

  const isEmpty = !(
    actions.remove ||
    actions.edit
  )

  const hide = () => {
    setOpen(false)
  }

  const emitAction = (action) => {
    hide()
    onAction(action)
  }

  if (isEmpty) {
    return null
  }

  const content = () => {
    return (
      <NodeActionItems>
        <If condition={actions.edit}>
          <NodeActionItem
            icon={<EditIcon />}
            onClick={() => emitAction('edit')}
            label={labels.edit}
          />
        </If>
        <If condition={actions.remove}>
          <NodeActionItem
            danger
            icon={<TrashCanIcon />}
            onClick={() => emitAction('remove')}
            label={labels.remove}
          />
        </If>
      </NodeActionItems>
    )
  }

  return (
    <NodeActionPopover
      trigger="click"
      visible={open}
      onVisibleChange={setOpen}
      content={content()}
      placement="rightTop"
    >
      <DeviceNodeAction className={open ? 'active' : ''}>
        <MoreIcon />
      </DeviceNodeAction>
    </NodeActionPopover>
  )
}

/**
 * Use to replace GroupNodeHeaderName
 */
export const EditableName = ({
  defaultName,
  onCancel,
  isLoading,
  onBlur,
  onSubmit,
}) => {
  const ref = useRef(null)

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      const name = (ref.current?.value || '').trim()

      if (name.trim() === '' || name === defaultName) {
        if (onCancel) {
          onCancel()
        }

        return
      }

      if (onSubmit) {
        onSubmit(name)
      }
    }
  }

  useEffect(() => {
    if (ref.current) {
      ref.current.focus()
    }
  }, [])

  return (
    <GroupNodeHeaderName className="group-node-header-name-input">
      <input
        ref={ref}
        readOnly={isLoading}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        autoFocus
        className=""
        defaultValue={defaultName}
      />
      {isLoading && (
        <Spin
          className="group-node-header-name-input-loading"
          indicator={<LoadingOutlined />}
        />
      )}
    </GroupNodeHeaderName>
  )
}

export const CreateGroup = ({
  onCancel,
  namespace,
  level,
  parentGroupId,
  onSuccess,
  placeholder,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const { send } = useAddSubgroup()

  const createGroup = async (name) => {
    try {
      setIsLoading(true)
      const newGroup = await send({
        namespace,
        parentDeviceGroupId: parentGroupId,
        name,
        type: 'public',
        deviceInfos: [],
      })

      setIsLoading(false)
      if (onSuccess) {
        onSuccess(newGroup)
      }
    } catch (error) {
      setIsLoading(false)
    }
  }

  return (
    <GroupNodeWrapper level={level} isRoot={level === 0}>
      <GroupNodeHeader>
        <GroupNodeHeaderLeft>
          <GroupNodeHeaderIcon>
            <PlusCircleFilled rotate={0} />
          </GroupNodeHeaderIcon>
          <EditableName
            defaultName={placeholder || ''}
            onCancel={onCancel}
            isLoading={isLoading}
            onSubmit={createGroup}
            onBlur={onCancel}
          />
        </GroupNodeHeaderLeft>
      </GroupNodeHeader>
    </GroupNodeWrapper>
  )
}

export const EditName = ({
  defaultName,
  groupId,
  namespace,
  onCancel,
  groupType,
  onSuccess,
}) => {
  const { send } = useUpdateDevice()
  const [isLoading, setIsLoading] = useState(false)

  const updateName = async (name) => {
    try {
      setIsLoading(true)
      await send({
        namespace,
        deviceGroupId: groupId,
        name,
        type: groupType,
      })

      setIsLoading(false)
      if (onSuccess) {
        onSuccess(groupId, name)
      }
    } catch (error) {
      setIsLoading(false)
    }
  }

  return (
    <EditableName
      defaultName={defaultName}
      onCancel={onCancel}
      isLoading={isLoading}
      onBlur={onCancel}
      onSubmit={updateName}
    />
  )
}
